/**
 * Platform-wide permission model.
 *
 * Permissions are fine-grained frontend capabilities derived from backend roles.
 * Roles come from JWT token claims; this module maps them to UI-level permissions.
 */

// Domain-scoped permission identifiers
export type Permission =
  // Platform-level
  | 'platform.view'
  // MWD / Werklijnen
  | 'mwd.view'
  | 'mwd.mutate'
  | 'mwd.defecten'
  | 'mwd.inregelingen'
  | 'mwd.opstelcapaciteit'
  // B-Regelingen
  | 'bregelingen.view'
  | 'bregelingen.accept'
  | 'bregelingen.close'
  // DDP / Plank
  | 'ddp.view'
  | 'ddp.mutate'
  // Agenda
  | 'agenda.view'
  | 'agenda.plan'
  | 'agenda.mutate'
  // Beheer
  | 'beheer.manage'
  | 'beheer.matbeheer'
  | 'beheer.tam'
  | 'beheer.solver'
  // LMM
  | 'lmm.view';

/**
 * Maps backend JWT role strings to frontend permissions.
 * A single role can grant multiple permissions (e.g., agenda_muteren grants both agenda.mutate and agenda.view).
 */
export const ROLE_PERMISSION_MAP: Record<string, Permission[]> = {
  bam_client_frontend_raadplegen: ['platform.view', 'mwd.view'],
  bam_client_frontend_muteren: ['mwd.mutate'],
  bam_client_frontend_defecten: ['mwd.defecten'],
  bam_client_frontend_bregelingen_meekijken: ['bregelingen.view'],
  bam_client_frontend_bregelingen_accepteren: ['bregelingen.accept'],
  bam_client_frontend_bregelingen_sluiten: ['bregelingen.close'],
  bam_client_frontend_ddp_meekijken: ['ddp.view'],
  bam_client_frontend_ddp_muteren: ['ddp.mutate'],
  bam_client_frontend_agenda_meekijken: ['agenda.view'],
  bam_client_frontend_agenda_muteren: ['agenda.mutate', 'agenda.view'],
  bam_client_frontend_agenda_plannen: ['agenda.plan', 'agenda.view'],
  bam_client_frontend_beheren: ['beheer.manage'],
  bam_client_frontend_matbeheren: ['beheer.matbeheer'],
  bam_client_frontend_tam: ['beheer.tam'],
  bam_client_frontend_inregelingen: ['mwd.inregelingen'],
  bam_client_frontend_solver: ['beheer.solver'],
  bam_client_frontend_opstelcapaciteit: ['mwd.opstelcapaciteit'],
  bam_client_frontend_lmm: ['lmm.view'],
};

/** All possible permissions (useful for "grant all" in dev mode) */
export const ALL_PERMISSIONS: Permission[] = [
  'platform.view',
  'mwd.view',
  'mwd.mutate',
  'mwd.defecten',
  'mwd.inregelingen',
  'mwd.opstelcapaciteit',
  'bregelingen.view',
  'bregelingen.accept',
  'bregelingen.close',
  'ddp.view',
  'ddp.mutate',
  'agenda.view',
  'agenda.plan',
  'agenda.mutate',
  'beheer.manage',
  'beheer.matbeheer',
  'beheer.tam',
  'beheer.solver',
  'lmm.view',
];
